import { useEffect, useState } from 'react';
import { creditsClient } from '../client/creditsClient';
import { useAuth } from '../contexts/AuthContext';
import { useOrgCredits } from './useOrgCredits';

/**
 * What a BOM will cost, said up front on Wizard Step 1. Extracting is free;
 * the BOM is charged per component when the buyer starts supplier discovery
 * on step 2, so the buyer is told the rule and their balance before they
 * commit to anything.
 *
 * The rate comes from the meter (quantity 1 prices one component), never a
 * constant in this file: rates are admin-editable, and a stale hardcoded
 * number would be the app lying about a price. When the estimate cannot be
 * read, `rate` stays null and the caller says nothing at all rather than
 * guessing.
 */

export const BOM_COMPONENT_USE_CASE = 'BOM_COMPONENT';

export interface BomPricing {
    /** Credits per component at discovery; null until known (or unreadable). */
    rate: number | null;
    /** Org balance; null while loading or when the credits API is unreachable. */
    balance: number | null;
    /**
     * True until BOTH the estimate and the balance have settled — success or
     * failure. A caller that must not act on an unknown (the step-2 credit
     * gate) waits on this; a failed read settles it to "unknown", not to
     * "pending forever".
     */
    pending: boolean;
}

export const useBomPricing = (): BomPricing => {
    const { backendUser } = useAuth();
    const orgId = backendUser?.orgId;
    const { credits, unreachable } = useOrgCredits();
    const [rate, setRate] = useState<number | null>(null);
    /** The org whose estimate has settled; compared to the current org so a change of org is pending again. */
    const [settledFor, setSettledFor] = useState<string | null>(null);

    useEffect(() => {
        if (!orgId) {
            setRate(null);
            return undefined;
        }
        let live = true;
        creditsClient
            .getEstimate(orgId, BOM_COMPONENT_USE_CASE, 1)
            .then((estimate) => {
                if (!live) return;
                setRate(estimate.rate);
                setSettledFor(orgId);
            })
            .catch(() => {
                // A disabled meter or an unreachable UMS both mean the same
                // thing to this screen: no price to state.
                if (!live) return;
                setRate(null);
                setSettledFor(orgId);
            });
        return () => {
            live = false;
        };
    }, [orgId]);

    // Signed out (no org) has nothing to wait for. Otherwise the balance has
    // settled once it was read or the read failed.
    const pending = Boolean(orgId) && (settledFor !== orgId || (credits === null && !unreachable));

    return { rate, balance: unreachable ? null : credits?.balance ?? null, pending };
};

export default useBomPricing;
